import React from 'react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import { SettingsToggle } from '../components/SettingsToggle';
import { SETTINGS_BTN_OUTLINE, SETTINGS_BTN_PRIMARY } from '../utils/settingsSectionStyles';

export function ConstructionDeductionsSettingsSection() {
  const [enabled, setEnabled] = React.useState(false);
  const [snapshot, setSnapshot] = React.useState<boolean | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const editing = snapshot != null;

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/settings/company', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error || 'Einstellungen konnten nicht geladen werden.');
        }
        return res.json() as Promise<{ construction_deductions_enabled?: boolean }>;
      })
      .then((data) => {
        if (!cancelled) setEnabled(data.construction_deductions_enabled === true);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Einstellungen konnten nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/settings/company', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ construction_deductions_enabled: enabled }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || 'Speichern fehlgeschlagen.');
      }
      setSnapshot(null);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard
      title="Umlage und Sicherheitseinbehalt"
      description="Aktiviert die VOB-/Bau-Abzüge in Rechnungen, Abschlags- und Schlussrechnungen. Bereits erfasste Umlagen und Einbehalte bleiben auch bei deaktivierter Einstellung sichtbar."
      actions={
        <>
          <SettingsCardSavedStatus saved={saved && !editing} />
          {editing ? (
            <>
              <button
                type="button"
                className={SETTINGS_BTN_OUTLINE}
                disabled={saving}
                onClick={() => {
                  setEnabled(snapshot);
                  setSnapshot(null);
                  setError(null);
                }}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className={SETTINGS_BTN_PRIMARY}
                disabled={saving || enabled === snapshot}
                onClick={() => void save()}
              >
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className={SETTINGS_BTN_OUTLINE}
              disabled={!loaded}
              onClick={() => {
                setSnapshot(enabled);
                setSaved(false);
                setError(null);
              }}
            >
              Bearbeiten
            </button>
          )}
        </>
      }
    >
      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      ) : null}
      {!loaded && !error ? <InlineLoadingState label="Einstellungen werden geladen …" /> : null}
      {loaded ? (
        <div className="flex items-center justify-between gap-4 rounded-md border border-app-border bg-app-surface-muted px-4 py-3">
          <div>
            <p className="text-sm font-medium text-app-text">Umlage und Sicherheitseinbehalt verwenden</p>
            <p className="mt-0.5 text-xs text-app-text-subtle">
              Zeigt die Eingabe im Dokumenteditor sowie Filter und Hinweise in den Rechnungsübersichten.
            </p>
          </div>
          <SettingsToggle
            active={enabled}
            disabled={!editing || saving}
            label="Umlage und Sicherheitseinbehalt verwenden"
            onClick={() => setEnabled((value) => !value)}
          />
        </div>
      ) : null}
    </SettingsCard>
  );
}
